import { Link } from "react-router-dom";

const values = [
  { title: "Engineering Precision", desc: "Corporate-grade rigor applied to every client engagement." },
  { title: "Fiduciary Accountability", desc: "Every recommendation stress-tested by a CPA, CIA, and engineer." },
  { title: "Living Benefit Focus", desc: "We engineer for life — access, liquidity, and growth while you're here." },
  { title: "Institutional Rigor", desc: "AOG and Experior backing means world-class carrier access for every client." },
];

export default function About() {
  return (
    <main className="pt-20">
      {/* Hero */}
      <section className="relative py-32 px-6 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1497366216548-37526070297c?w=1920&q=80"
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-navy-dark/95 via-navy/90 to-navy" />
        </div>
        <div className="relative max-w-3xl mx-auto text-center">
          <a
            href="https://experiorfinancial.com/"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 border border-gold/30 bg-gold/10 text-gold text-xs tracking-widest uppercase px-4 py-2 rounded-full mb-6 hover:bg-gold/20 transition-colors"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-gold inline-block" />
            Operating Under Experior Financial Group
          </a>
          <h1 className="font-display text-5xl md:text-6xl font-bold text-white mb-4">
            We Are Financial Engineers,<br />
            <span className="text-gold">Not Salespeople.</span>
          </h1>
          <span className="gold-line mx-auto" />
          <p className="text-white/55 text-lg max-w-xl mx-auto">
            High-income earners deserve the same financial sophistication wielded by corporations. We're uniquely qualified to deliver it.
          </p>
        </div>
      </section>

      {/* Founder section */}
      <section className="py-24 px-6">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          {/* Founder image card */}
          <div className="flex flex-col items-center text-center lg:items-start lg:text-left">
            <img
              src="/PHOTO-2026-04-10-17-17-12.jpg"
              alt="Nadine Stephens, Founder of NLS Financial Group"
              className="w-80 h-80 object-contain"
            />
            <h2 className="font-display text-2xl font-bold text-navy mt-4 mb-1">Nadine Stephens, CPA, CIA</h2>
            <p className="text-gold text-xs tracking-widest uppercase">Strategic Wealth Partner · Agency Owner</p>
            <p className="text-gray-400 text-xs mt-1">Experior Financial Group Network</p>
          </div>

          <div>
            <p className="text-gold text-xs tracking-widest uppercase mb-3">Meet the Founder</p>
            <h2 className="section-title">A Wealth Architect With 30+ Years of Elite Experience</h2>
            <span className="gold-line" />
            <p className="text-gray-600 leading-relaxed mb-4">
              With over 30 years of elite finance and audit experience, Nadine Stephens isn't just a financial professional — she is a Wealth Architect. Nadine built her career on the "Big 4" rigor of PricewaterhouseCoopers (PwC) and Ernst & Young (EY), serving as a trusted advisor to government entities and global corporations. Today, she has transitioned from the corporate boardroom to the community, pivoting her expertise to empower families and entrepreneurs.
            </p>

            <p className="text-gold text-xs tracking-widest uppercase mb-3 mt-6">Why Nadine?</p>
            <p className="text-gray-600 text-sm leading-relaxed mb-4">
              As a licensed CPA and Certified Internal Auditor, Nadine looks at wealth through a lens of precision and protection. She specializes in:
            </p>
            <ul className="space-y-3 mb-6">
              {[
                { label: "Tax-Advantaged Architecture", desc: "Using CPA-level insight to keep more money in your pocket." },
                { label: "Asset Protection", desc: "Applying audit-grade risk management to shield your legacy." },
                { label: "Strategic Growth", desc: "Designing Indexed Universal Life (IUL) and retirement strategies with mathematical certainty." },
              ].map((item) => (
                <li key={item.label} className="flex items-start gap-3 text-sm text-gray-600">
                  <span className="text-gold mt-1 shrink-0">✦</span>
                  <span><span className="font-semibold text-navy">{item.label}:</span> {item.desc}</span>
                </li>
              ))}
            </ul>
            <p className="text-gray-600 leading-relaxed mb-8">
              Nadine is now leading a high-performance agency, dedicated to teaching the "rules of money" that were once reserved only for the elite. Whether she is designing your financial future or mentoring the next generation of financial leaders, Nadine brings a standard of excellence that is unmatched in the industry.
            </p>
            <Link to="/contact" className="btn-gold">Work With Nadine</Link>
          </div>
        </div>
      </section>

      {/* Network — image cards */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-gold text-xs tracking-widest uppercase mb-3">Our Network</p>
            <h2 className="section-title">Backed by Institutional Strength</h2>
            <span className="gold-line mx-auto" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <a href="https://experiorfinancial.com/" target="_blank" rel="noreferrer" className="group relative rounded-2xl overflow-hidden h-64 shadow-xl block">
              <img
                src="https://images.unsplash.com/photo-1486325212027-8081e485255e?w=800&q=80"
                alt="Experior Financial Group"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy-dark/90 via-navy-dark/30 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <p className="text-gold text-xs tracking-widest uppercase mb-1">Premier North American IMO</p>
                <h3 className="font-display text-xl font-bold text-white mb-1">Experior Financial Group</h3>
                <p className="text-white/65 text-sm">World-class carrier relationships and institutional stability.</p>
                <span className="text-gold text-xs mt-2 inline-block">experiorfinancial.com →</span>
              </div>
            </a>
            <a href="https://associateownersgroup.com/" target="_blank" rel="noreferrer" className="group relative rounded-2xl overflow-hidden h-64 shadow-xl block">
              <img
                src="https://images.unsplash.com/photo-1560179707-f14e90ef3623?w=800&q=80"
                alt="Associate Owners Group"
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy-dark/90 via-navy-dark/30 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <p className="text-gold text-xs tracking-widest uppercase mb-1">Global IMO Alliance · IPO-Ready</p>
                <h3 className="font-display text-xl font-bold text-white mb-1">Associate Owners Group (AOG)</h3>
                <p className="text-white/65 text-sm">IPO-targeted global scale positioning clients at the frontier.</p>
                <span className="text-gold text-xs mt-2 inline-block">associateownersgroup.com →</span>
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-gold text-xs tracking-widest uppercase mb-3">Our Principles</p>
            <h2 className="section-title">What Drives Every Decision</h2>
            <span className="gold-line mx-auto" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {values.map((v) => (
              <div key={v.title} className="card-dark">
                <h3 className="font-display text-xl font-semibold text-gold mb-2">{v.title}</h3>
                <p className="text-gray-600 text-sm">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Full-width CTA */}
      <section className="relative h-80 overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1551836022-d5d88e9218df?w=1920&q=80"
          alt=""
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-navy-dark/80 flex items-center justify-center text-center px-6">
          <div>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-4">
              Ready to <span className="text-gold">Work Together?</span>
            </h2>
            <Link to="/contact" className="btn-gold">Book a Strategy Audit</Link>
          </div>
        </div>
      </section>
    </main>
  );
}

